import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import './suboutRegistration.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect, TRACKING_RANGES } from '../../modules/DateRangeSelect';
import { DEFAULT_DATE_RANGE } from '../../../components/listDefaults';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import { apiMessage, fetchSuboutLookups, fetchSuboutQueue } from './api';
import { rowKey, type SuboutLookups, type SuboutRegistrationRow } from './types';
import SuboutRegistrationDetail from './SuboutRegistrationDetail';

// ─────────────────────────────────────────────────────────────────────────────
// Sample SubContract Tracking → Subout Registration (nav `suboutreg`).
// Legacy: SuboutSampleRegistrationViewController /
// SampleParameter_ListView_Copy_SubOutPendingSamples.
//
// The queue of subout-flagged sample tests (SampleParameter.SubOut = 1) awaiting
// — or already placed into — a subout order. "New" takes the selected PENDING
// rows (StatusId SS-1-1 "Pending Subcontract"), asks for a contract lab + TAT,
// and the API creates ONE SubOutSampleRegistrations order, links the tests, and
// advances them to SS-1-2 "Pending Submission". See
// deploy/db/AI_SuboutRegistration.sql. The API does the work — this screen only
// selects, collects the order details, and confirms.
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Subout Registration';

type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

function sinceFromKey(k: DateRangeKey): string | null {
  if (k === 'All') return null;
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const COLUMNS: AppColumn<SuboutRegistrationRow>[] = [
  { field: 'jobId',        header: 'JobID',        width: 110, text: (r) => r.jobId ?? '', render: (r) => r.jobId || '—' },
  { field: 'sampleId',     header: 'SampleID',     width: 130, text: (r) => r.sampleId ?? '', render: (r) => r.sampleId || '—' },
  { field: 'sampleName',   header: 'SampleName',   width: 150, text: (r) => r.sampleName ?? '', render: (r) => r.sampleName || '—' },
  { field: 'sampleMatrix', header: 'SampleMatrix', width: 120, text: (r) => r.sampleMatrix ?? '', render: (r) => r.sampleMatrix || '—' },
  { field: 'test',         header: 'Test',         width: 170, text: (r) => r.test ?? '', render: (r) => r.test || '—' },
  { field: 'method',       header: 'Method',       width: 120, text: (r) => r.method ?? '', render: (r) => r.method || '—' },
  { field: 'parameter',    header: 'Parameter',    width: 170, text: (r) => r.parameter ?? '', render: (r) => r.parameter || '—' },
  { field: 'dueDate',      header: 'DueDate',      width: 110, type: 'date', text: (r) => fmtLocalDate(r.dueDate), render: (r) => fmtLocalDate(r.dueDate) || '—' },
  { field: 'dateReceived', header: 'DateReceived', width: 140, type: 'datetime', text: (r) => fmtLocalDateTime(r.dateReceived), render: (r) => fmtLocalDateTime(r.dateReceived) || '—' },
  { field: 'client',       header: 'Client',       width: 190, text: (r) => r.client ?? '', render: (r) => r.client || '—' },
  { field: 'status',       header: 'Status',       width: 160, type: 'status', text: (r) => r.status ?? '' },
  { field: 'statusId',     header: 'StatusID',     width: 80, text: (r) => r.statusId ?? '', render: (r) => r.statusId || '—' },
];

const FIND_FIELDS: (keyof SuboutRegistrationRow)[] =
  ['jobId', 'sampleId', 'sampleName', 'test', 'method', 'parameter', 'client', 'status'];

export default function SuboutRegistrationPage() {
  const notify = useNotify();
  // ALPACA Module Access Control (MAC): New / register needs the Create right.
  // A Read-only user sees the queue but every action is greyed. Falls open for
  // admins and where no MAC level is configured (useNavActions).
  const perm = useNavActions('suboutreg');

  const [rows, setRows]       = useState<SuboutRegistrationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.SuboutTracking as DateRangeKey);
  const [search, setSearch]   = useState('');
  const [statusChip, setStatusChip] = useState<string>('all');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchSuboutQueue(sinceFromKey(dateRange), null));
      setSelected(new Set());
    } catch (e) {
      notify.error(apiMessage(e, 'Failed to load the subout registration queue.'));
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [dateRange, notify]);

  useEffect(() => { void load(); }, [load]);

  // Registration-dialog lookups — fetched once, quietly.
  const [lookups, setLookups] = useState<SuboutLookups | null>(null);
  useEffect(() => {
    fetchSuboutLookups()
      .then(setLookups)
      .catch((e) => notify.error(apiMessage(e, 'Failed to load the registration lookups.')));
  }, [notify]);

  // Search + status-chip filter (the grid does per-column filter / sort / paging).
  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => FIND_FIELDS.some((f) => String(r[f] ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const visible = useMemo(() => (statusChip === 'all'
    ? searchFiltered
    : searchFiltered.filter((r) => r.status === statusChip)), [searchFiltered, statusChip]);

  const chips = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of searchFiltered) {
      const k = r.status ?? '—';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [searchFiltered]);

  // All selected rows; only the Pending Subcontract ones can be registered.
  const picked = useMemo(
    () => visible.filter((r) => selected.has(rowKey(r))),
    [visible, selected]);
  const pickedPending = useMemo(() => picked.filter((r) => r.isPending), [picked]);

  // ── Registration detail view (full page — REPLACES the list, standing rule 12) ──
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSamples, setDetailSamples] = useState<SuboutRegistrationRow[]>([]);

  function openDialog() {
    if (!perm.create) {
      notify.warning('You do not have permission to register subout samples.');
      return;
    }
    // ALPACA parity (NewObjectAction_Executing, line 4824): register only Pending
    // Subcontract rows — abort if nothing is selected, or any selected row is
    // already registered. Verbatim legacy message.
    if (picked.length === 0 || picked.some((r) => !r.isPending)) {
      notify.warning('Select the Pending Subout Samples');
      return;
    }
    setDetailSamples(pickedPending);
    setDetailOpen(true);
  }

  // New opens the full-page detail view (like ALPACA's
  // SubOutSampleRegistrations_DetailView_Copy) — the list is replaced by it.
  if (detailOpen) {
    return (
      <SuboutRegistrationDetail
        samples={detailSamples}
        lookups={lookups}
        canCreate={perm.create}
        onClose={() => setDetailOpen(false)}
        onSaved={() => { setDetailOpen(false); void load(); }}
      />
    );
  }

  return (
    <div className="bl-page">
      <FormTitle pageId="suboutreg" loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />

      <div className="bl-grid-area">
        {/* Toolbar — canonical order: [Date Range] · New · Refresh · Export to. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                value={TRACKING_RANGES.find((r) => r.label === dateRange)?.days ?? null}
                onChange={(d) => setDateRange((TRACKING_RANGES.find((r) => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            }
            onNew={openDialog}
            newLabel="New"
            newDisabled={loading}
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'subout-registration', ...gridExportMatrix(visible, COLUMNS) }}
            perm={perm}
          />
        </div>

        <div className="list-search-row">
          <GridSearch value={search} onChange={setSearch} placeholder="Search job, sample, test, client…" />
          {picked.length > 0 && (
            <span className="search-match-count">{picked.length} selected</span>
          )}
        </div>

        <div className="bl-card bl-card-flex">
          {chips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Status:</span>
              <button className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                      onClick={() => { setStatusChip('all'); setSelected(new Set()); }}>
                All ({searchFiltered.length})
              </button>
              {chips.map(([label, n]) => (
                <button key={label} className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                        onClick={() => { setStatusChip(label); setSelected(new Set()); }}>
                  {label} ({n})
                </button>
              ))}
            </div>
          )}

          <AppDataGrid<SuboutRegistrationRow>
            rows={visible}
            columns={COLUMNS}
            getRowId={rowKey}
            loading={loading}
            loadMs={loadMs}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            emptyText="No subout-flagged samples for this date range."
          />
        </div>
      </div>
    </div>
  );
}
